import * as React from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { isTourTargetInView, scrollTourTargetIntoView } from "./scrollTourTarget";
import { tourProgressDots } from "./tourProgressDots";
import type { DemoHintPlacement, DemoHintStep } from "./types";

export type TourOverlayProps = {
  step: DemoHintStep;
  stepIndex: number;
  stepCount: number;
  targetElement: HTMLElement | null;
  /** When true, target is not ready — show skip-only chrome without the step tooltip. */
  isWaitingForTarget?: boolean;
  onSkip: () => void;
  onNext: () => void;
};

function TourSkipOnlyBar({ onSkip }: { onSkip: () => void }) {
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-[1200] flex justify-center px-4">
      <div className="pointer-events-auto rounded-l border border-primary-300 bg-neutral-background-100 px-4 py-2 shadow-l">
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="text-neutral-text-500 hover:text-neutral-text-800"
          onClick={onSkip}
        >
          Пропустить тур
        </Button>
      </div>
    </div>,
    document.body,
  );
}

/** Extra hit area so passthrough hover stays stable while labels expand. */
const PASSTHROUGH_HIT_PAD_PX = 12;
/** Delay closing the shield hole so brief pointer gaps do not flicker layout. */
const PASSTHROUGH_CLOSE_DELAY_MS = 180;

function pointInRect(
  clientX: number,
  clientY: number,
  rect: DOMRect,
  pad = 0,
): boolean {
  return (
    clientX >= rect.left - pad &&
    clientX <= rect.right + pad &&
    clientY >= rect.top - pad &&
    clientY <= rect.bottom + pad
  );
}

function clickThroughShield(
  shield: HTMLElement,
  clientX: number,
  clientY: number,
  root: HTMLElement,
): boolean {
  shield.style.pointerEvents = "none";
  const underneath = document.elementFromPoint(clientX, clientY);
  shield.style.pointerEvents = "auto";
  if (
    underneath instanceof HTMLElement &&
    (underneath === root || root.contains(underneath))
  ) {
    underneath.click();
    return true;
  }
  return false;
}

/**
 * Transparent full-screen shield: swallows clicks except tour tooltip controls,
 * optional click-gated tour targets, and elements marked data-tour-passthrough
 * (e.g. demo mode switcher — hoverable + clickable, never highlighted).
 *
 * Passthrough uses a temporary pointer-events hole so CSS :hover / group-hover
 * reach the switcher (labels expand). Clicks then hit the element natively.
 */
function TourClickShield({
  targetElement,
  tooltipRootRef,
  allowTargetClick,
}: {
  targetElement: HTMLElement | null;
  tooltipRootRef: React.RefObject<HTMLElement | null>;
  allowTargetClick: boolean;
}) {
  const shieldRef = React.useRef<HTMLDivElement | null>(null);
  const passthroughOpenRef = React.useRef(false);
  const closePassthroughTimerRef = React.useRef<number | null>(null);

  const findPassthroughAt = React.useCallback(
    (clientX: number, clientY: number): HTMLElement | null => {
      const nodes = document.querySelectorAll("[data-tour-passthrough]");
      for (const node of nodes) {
        if (!(node instanceof HTMLElement)) continue;
        // Overlay-expanded labels overflow the sizer box — hit-test the visual
        // control as well so hover does not collapse mid-expand.
        const control = node.querySelector("[data-slot='segmented-control']");
        const rects = [
          node.getBoundingClientRect(),
          ...(control instanceof HTMLElement
            ? [control.getBoundingClientRect()]
            : []),
        ];
        if (rects.some((rect) => pointInRect(clientX, clientY, rect, PASSTHROUGH_HIT_PAD_PX))) {
          return node;
        }
      }
      // While hole is open, prefer elementFromPoint so an expanded switcher
      // still counts even if the pointer is slightly outside the old rect.
      if (passthroughOpenRef.current) {
        const under = document.elementFromPoint(clientX, clientY);
        if (under instanceof Node) {
          for (const node of nodes) {
            if (
              node instanceof HTMLElement &&
              (under === node || node.contains(under))
            ) {
              return node;
            }
          }
        }
      }
      return null;
    },
    [],
  );

  const isOverPassthrough = React.useCallback(
    (clientX: number, clientY: number) =>
      findPassthroughAt(clientX, clientY) != null,
    [findPassthroughAt],
  );

  const closePassthroughHole = React.useCallback(() => {
    const shield = shieldRef.current;
    if (!shield || !passthroughOpenRef.current) return;
    passthroughOpenRef.current = false;
    shield.style.pointerEvents = "auto";
  }, []);

  const cancelPassthroughClose = React.useCallback(() => {
    if (closePassthroughTimerRef.current != null) {
      window.clearTimeout(closePassthroughTimerRef.current);
      closePassthroughTimerRef.current = null;
    }
  }, []);

  const schedulePassthroughClose = React.useCallback(() => {
    cancelPassthroughClose();
    closePassthroughTimerRef.current = window.setTimeout(() => {
      closePassthroughTimerRef.current = null;
      closePassthroughHole();
    }, PASSTHROUGH_CLOSE_DELAY_MS);
  }, [cancelPassthroughClose, closePassthroughHole]);

  const openPassthroughHole = React.useCallback(() => {
    const shield = shieldRef.current;
    if (!shield || passthroughOpenRef.current) return;
    cancelPassthroughClose();
    passthroughOpenRef.current = true;
    shield.style.pointerEvents = "none";
  }, [cancelPassthroughClose]);

  React.useEffect(() => {
    const onDocumentPointerMove = (event: PointerEvent) => {
      if (!passthroughOpenRef.current) return;
      if (isOverPassthrough(event.clientX, event.clientY)) {
        cancelPassthroughClose();
        return;
      }
      schedulePassthroughClose();
    };
    document.addEventListener("pointermove", onDocumentPointerMove);
    return () => {
      document.removeEventListener("pointermove", onDocumentPointerMove);
      cancelPassthroughClose();
      closePassthroughHole();
    };
  }, [
    cancelPassthroughClose,
    closePassthroughHole,
    isOverPassthrough,
    schedulePassthroughClose,
  ]);

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent) => {
      if (passthroughOpenRef.current) return;
      if (!isOverPassthrough(event.clientX, event.clientY)) return;
      openPassthroughHole();
    },
    [isOverPassthrough, openPassthroughHole],
  );

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent) => {
      const { clientX, clientY } = event;
      const shield = event.currentTarget as HTMLElement;

      const tooltipRoot = tooltipRootRef.current;
      if (tooltipRoot && pointInRect(clientX, clientY, tooltipRoot.getBoundingClientRect())) {
        return;
      }

      // Passthrough may already have a hole (hover); otherwise click-through.
      const passthrough = findPassthroughAt(clientX, clientY);
      if (passthrough) {
        clickThroughShield(shield, clientX, clientY, passthrough);
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (allowTargetClick && targetElement) {
        const rect = targetElement.getBoundingClientRect();
        if (pointInRect(clientX, clientY, rect)) {
          shield.style.pointerEvents = "none";
          const underneath = document.elementFromPoint(clientX, clientY);
          shield.style.pointerEvents = passthroughOpenRef.current
            ? "none"
            : "auto";
          if (
            underneath instanceof HTMLElement &&
            (underneath === targetElement || targetElement.contains(underneath))
          ) {
            targetElement.click();
          }
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      event.preventDefault();
      event.stopPropagation();
    },
    [allowTargetClick, findPassthroughAt, targetElement, tooltipRootRef],
  );

  return createPortal(
    <div
      ref={shieldRef}
      aria-hidden
      className="fixed inset-0 z-[1100]"
      style={{ pointerEvents: "auto" }}
      onPointerMove={onPointerMove}
      onPointerDown={onPointerDown}
    />,
    document.body,
  );
}

/** Side-placed tips on the detail page need a tighter card to stay on-screen. */
const NARROW_TOOLTIP_TARGETS = new Set([
  "designer-client",
  "designer-meeting-flags",
  "designer-meeting-summary",
  "designer-criterion",
  "designer-issues",
  "designer-improve",
]);

function TourTooltipCard({
  step,
  stepIndex,
  stepCount,
  onSkip,
  onNext,
}: {
  step: DemoHintStep;
  stepIndex: number;
  stepCount: number;
  onSkip: () => void;
  onNext: () => void;
}) {
  // expand-profile and other showNext+click steps keep action:"click"
  // (highlight + click-to-advance) but still show Next; Next triggers the same
  // target click so side effects run before advancing.
  const showNext = !step.action || Boolean(step.showNext);
  const stepLabel = `Шаг ${stepIndex + 1} из ${stepCount}`;
  const dots = tourProgressDots(stepIndex, stepCount);
  const narrow = NARROW_TOOLTIP_TARGETS.has(step.target);

  return (
    <div
      className={cn(
        "flex flex-col gap-3 p-4",
        narrow
          ? "w-[min(100vw-2rem,15.5rem)]"
          : "w-[min(100vw-2rem,22rem)]",
      )}
    >
      <div className="text-caption font-medium uppercase tracking-wide text-primary-300">
        {stepLabel}
      </div>
      <div className="flex min-w-0 flex-col gap-2">
        <h2 className="text-balance text-h3 font-medium text-neutral-text-900">
          {step.title}
        </h2>
        <p className="text-pretty text-body2 text-neutral-text-500">{step.text}</p>
      </div>
      {/* Stack footer so skip / dots / next never overlap when the card is narrow. */}
      <div className="flex flex-col gap-2 pt-1">
        <div className="flex items-center justify-center gap-1.5" aria-hidden>
          {dots.map((dot) => (
            <span
              key={dot.stepIndex}
              className={cn(
                "rounded-full",
                dot.truncated ? "size-1 opacity-40" : "size-1.5",
                dot.active ? "bg-primary-300 opacity-100" : "bg-neutral-border-300",
              )}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            className="shrink-0 text-neutral-text-500 hover:text-neutral-text-800"
            onClick={onSkip}
          >
            Пропустить тур
          </Button>
          {showNext ? (
            <Button type="button" size="sm" className="shrink-0" onClick={onNext}>
              Далее
            </Button>
          ) : (
            <span className="w-[4.5rem] shrink-0" aria-hidden />
          )}
        </div>
      </div>
    </div>
  );
}

function subscribeLayout(update: () => void): () => void {
  window.addEventListener("resize", update);
  window.addEventListener("scroll", update, true);
  window.addEventListener("scrollend", update, true);
  const fallback = window.setTimeout(update, 320);
  return () => {
    window.removeEventListener("resize", update);
    window.removeEventListener("scroll", update, true);
    window.removeEventListener("scrollend", update, true);
    window.clearTimeout(fallback);
  };
}

/** Outset so the click-target ring sits just outside the button edge (~4px). */
const HIGHLIGHT_OUTSET_PX = 4;

function readBorderRadius(element: HTMLElement): string {
  return getComputedStyle(element).borderRadius;
}

/**
 * Blue ring around a click-gated tour target. Pointer-events stay none so
 * TourClickShield still owns hit-testing.
 */
function TourTargetHighlight({ borderRadius }: { borderRadius: string }) {
  return (
    <div
      data-tour-target-highlight
      className="pointer-events-none absolute animate-pulse"
      style={{
        inset: -HIGHLIGHT_OUTSET_PX,
        borderRadius,
        boxShadow: "0 0 0 2px var(--color-primary-300), 0 0 10px 2px var(--color-primary-300)",
      }}
      aria-hidden
    />
  );
}

/**
 * Virtual anchor wrapper so Popover can position relative to an arbitrary DOM node.
 */
function VirtualAnchor({
  element,
  highlightTarget = false,
  children,
}: {
  element: HTMLElement;
  highlightTarget?: boolean;
  children: React.ReactNode;
}) {
  const [box, setBox] = React.useState(() => element.getBoundingClientRect());
  const [borderRadius, setBorderRadius] = React.useState(() =>
    highlightTarget ? readBorderRadius(element) : "",
  );

  React.useLayoutEffect(() => {
    const update = () => {
      const next = element.getBoundingClientRect();
      setBox((prev) => {
        if (
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.height === next.height
        ) {
          return prev;
        }
        return next;
      });
      if (highlightTarget) {
        const nextRadius = readBorderRadius(element);
        setBorderRadius((prev) => (prev === nextRadius ? prev : nextRadius));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(element);
    const unsub = subscribeLayout(update);
    const raf = requestAnimationFrame(() => {
      update();
      requestAnimationFrame(update);
    });
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      unsub();
    };
  }, [element, highlightTarget]);

  return (
    <>
      {createPortal(
        <div
          data-tour-virtual-anchor
          style={{
            position: "fixed",
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
            pointerEvents: "none",
            zIndex: 1101,
          }}
          aria-hidden
        >
          {highlightTarget ? <TourTargetHighlight borderRadius={borderRadius} /> : null}
          {children}
        </div>,
        document.body,
      )}
    </>
  );
}

const HEADER_END_ALIGN_TARGETS = new Set([
  "notifications",
  "add-questionnaire",
  "dashboard-date-range",
]);

/** Tall left-side content hints: pin tooltip top to the title/label. */
const CONTENT_START_ALIGN_TARGETS = new Set([
  "short-recommendation",
  "short-profile",
  "long-recommendation",
  "long-profile",
  "crm-product-groups",
  "checkpoints",
  "visit-card",
  "checkpoint-additional-services",
  "meeting-results-body",
  "meeting-timecodes",
  "designer-overview",
  "designer-client",
  "designer-meeting-summary",
  "designer-criterion",
  "designer-issues",
  "designer-improve",
]);

function popoverAlignForTarget(target: string): "start" | "center" | "end" {
  if (HEADER_END_ALIGN_TARGETS.has(target)) return "end";
  if (CONTENT_START_ALIGN_TARGETS.has(target)) return "start";
  return "center";
}

/** Keep caret tip inside the tooltip edge (size-3 diamond ≈ 12px). */
const CARET_EDGE_PAD = 10;

const PLACEMENTS = new Set<DemoHintPlacement>(["top", "bottom", "left", "right"]);

function readPlacedSide(
  contentEl: HTMLElement | null,
  fallback: DemoHintPlacement,
): DemoHintPlacement {
  const raw = contentEl?.getAttribute("data-side");
  if (raw && PLACEMENTS.has(raw as DemoHintPlacement)) {
    return raw as DemoHintPlacement;
  }
  return fallback;
}

/**
 * Offset (px from tooltip top-left) so the caret tip sits on the target center,
 * even when Floating UI shifts the popover for collision avoidance.
 * Uses the *actual* placed side (`data-side`), not only the preferred placement.
 */
function useCaretTowardTarget(
  targetElement: HTMLElement,
  tooltipRootRef: React.RefObject<HTMLElement | null>,
  contentRef: React.RefObject<HTMLElement | null>,
  preferredSide: DemoHintPlacement,
): { main: number | null; side: DemoHintPlacement } {
  const [main, setMain] = React.useState<number | null>(null);
  const [side, setSide] = React.useState<DemoHintPlacement>(preferredSide);

  React.useLayoutEffect(() => {
    setSide(preferredSide);
  }, [preferredSide]);

  React.useLayoutEffect(() => {
    const update = () => {
      const tip = tooltipRootRef.current;
      if (!tip) return;
      const placed = readPlacedSide(contentRef.current, preferredSide);
      setSide((prev) => (prev === placed ? prev : placed));

      const tr = tip.getBoundingClientRect();
      const ar = targetElement.getBoundingClientRect();
      let raw: number;
      let max: number;
      if (placed === "top" || placed === "bottom") {
        raw = ar.left + ar.width / 2 - tr.left;
        max = tr.width;
      } else {
        raw = ar.top + ar.height / 2 - tr.top;
        max = tr.height;
      }
      const clamped = Math.min(
        Math.max(raw, CARET_EDGE_PAD),
        Math.max(CARET_EDGE_PAD, max - CARET_EDGE_PAD),
      );
      setMain((prev) => (prev === clamped ? prev : clamped));
    };

    update();
    const raf = requestAnimationFrame(() => {
      update();
      requestAnimationFrame(update);
    });
    const unsub = subscribeLayout(update);
    const ro = new ResizeObserver(update);
    ro.observe(targetElement);
    const tip = tooltipRootRef.current;
    if (tip) ro.observe(tip);
    const content = contentRef.current;
    if (content) {
      ro.observe(content);
      const mo = new MutationObserver(update);
      mo.observe(content, { attributes: true, attributeFilter: ["data-side", "style"] });
      return () => {
        cancelAnimationFrame(raf);
        unsub();
        ro.disconnect();
        mo.disconnect();
      };
    }
    return () => {
      cancelAnimationFrame(raf);
      unsub();
      ro.disconnect();
    };
  }, [targetElement, tooltipRootRef, contentRef, preferredSide]);

  return { main, side };
}

function TourPopover({
  step,
  stepIndex,
  stepCount,
  targetElement,
  onSkip,
  onNext,
  tooltipRootRef,
}: {
  step: DemoHintStep;
  stepIndex: number;
  stepCount: number;
  targetElement: HTMLElement;
  onSkip: () => void;
  onNext: () => void;
  tooltipRootRef: React.RefObject<HTMLDivElement | null>;
}) {
  const preferredSide = step.placement;
  const align = popoverAlignForTarget(step.target);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const { main: caretMain, side } = useCaretTowardTarget(
    targetElement,
    tooltipRootRef,
    contentRef,
    preferredSide,
  );

  const isVertical = side === "top" || side === "bottom";
  const caretOffsetStyle: React.CSSProperties = isVertical
    ? { left: caretMain ?? "50%", transform: "translateX(-50%)" }
    : { top: caretMain ?? "50%", transform: "translateY(-50%)" };

  // On click-gated steps that still show Next (expand-profile), Next must activate
  // the target so side effects (e.g. expand) run; the tour listener advances via click.
  const handleNext = React.useCallback(() => {
    if (step.action === "click") {
      targetElement.click();
      return;
    }
    onNext();
  }, [onNext, step.action, targetElement]);

  return (
    <VirtualAnchor element={targetElement} highlightTarget={Boolean(step.action)}>
      <Popover open>
        <PopoverAnchor asChild>
          <div className="h-full w-full" />
        </PopoverAnchor>
        <PopoverContent
          ref={contentRef}
          side={preferredSide}
          align={align}
          sideOffset={12}
          collisionPadding={24}
          className={cn(
            "z-[1200] max-w-[min(100vw-2rem,22rem)] border-primary-300 bg-neutral-background-100 p-0 shadow-l",
            NARROW_TOOLTIP_TARGETS.has(step.target) &&
              "max-w-[min(100vw-2rem,15.5rem)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
          )}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <div ref={tooltipRootRef} className="relative">
            <TourCaret side={side} offsetStyle={caretOffsetStyle} />
            <TourTooltipCard
              step={step}
              stepIndex={stepIndex}
              stepCount={stepCount}
              onSkip={onSkip}
              onNext={handleNext}
            />
          </div>
        </PopoverContent>
      </Popover>
    </VirtualAnchor>
  );
}

/**
 * Mask hides the card border under the caret; outer/inner diamonds draw a
 * continuous arrow outline that still looks clean when offset from center.
 */
function TourCaret({
  side,
  offsetStyle,
}: {
  side: DemoHintPlacement;
  offsetStyle: React.CSSProperties;
}) {
  return (
    <div
      className={cn(
        "pointer-events-none absolute z-10",
        side === "bottom" && "-top-1.5",
        side === "top" && "-bottom-1.5",
        side === "left" && "-right-1.5",
        side === "right" && "-left-1.5",
      )}
      style={offsetStyle}
      aria-hidden
    >
      {/* Erase the PopoverContent border (and the diamond half inside the card). */}
      <div
        className={cn(
          "absolute bg-neutral-background-100",
          side === "bottom" && "left-1/2 top-[5px] h-2 w-3.5 -translate-x-1/2",
          side === "top" && "bottom-[5px] left-1/2 h-2 w-3.5 -translate-x-1/2",
          side === "left" && "top-1/2 right-[5px] h-3.5 w-2 -translate-y-1/2",
          side === "right" && "top-1/2 left-1.5 h-3.5 w-2 -translate-y-1/2",
        )}
      />
      {/* Outer diamond = border color */}
      <div className="relative size-3 rotate-45 bg-primary-300">
        {/* Inner diamond = card fill (1px outline) */}
        <div className="absolute inset-px bg-neutral-background-100" />
      </div>
    </div>
  );
}

/**
 * Hold the tooltip until the target is on-screen. Off-screen targets scroll
 * first; skip chrome stays available while alignment runs.
 */
function useTourHintReady(targetElement: HTMLElement | null, stepId: string): boolean {
  const [readyTarget, setReadyTarget] = React.useState<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    if (!targetElement) {
      setReadyTarget(null);
      return;
    }
    if (isTourTargetInView(targetElement)) {
      setReadyTarget(targetElement);
      return;
    }
    setReadyTarget(null);
    const controller = new AbortController();
    void scrollTourTargetIntoView(targetElement, controller.signal).then(() => {
      if (!controller.signal.aborted) setReadyTarget(targetElement);
    });
    return () => controller.abort();
  }, [targetElement, stepId]);

  return readyTarget === targetElement && targetElement !== null;
}

export function TourOverlay({
  step,
  stepIndex,
  stepCount,
  targetElement,
  isWaitingForTarget = false,
  onSkip,
  onNext,
}: TourOverlayProps) {
  const tooltipRootRef = React.useRef<HTMLDivElement | null>(null);
  const hintReady = useTourHintReady(targetElement, step.id);
  const allowTargetClick = Boolean(step.action) && hintReady && Boolean(targetElement);

  return (
    <>
      <TourClickShield
        targetElement={allowTargetClick ? targetElement : null}
        tooltipRootRef={tooltipRootRef}
        allowTargetClick={allowTargetClick}
      />
      {targetElement && hintReady ? (
        <TourPopover
          step={step}
          stepIndex={stepIndex}
          stepCount={stepCount}
          targetElement={targetElement}
          onSkip={onSkip}
          onNext={onNext}
          tooltipRootRef={tooltipRootRef}
        />
      ) : isWaitingForTarget || targetElement ? (
        <TourSkipOnlyBar onSkip={onSkip} />
      ) : null}
    </>
  );
}
